import React from 'react';
import { GuidePanel, Note, SubHeading } from '../GuidePanel';
import { MARLON_REFERENCE_URL } from '../../../data/guide/evolutions';
import { ValidationBadge, ValidationSummary } from '../ValidationBadge';
import { QuestBlock } from '../quest/QuestBlock';
import { useNpcLocation } from '../../../hooks/useNpcLocation';
import { npcLocationValidation } from '../../../utils/npcLocation';
import { marlon } from '../../../data/guide/evolutions';
import { questTitle } from '../../../data/guide/quests';
import type { Checklist } from '../../../hooks/useChecklist';
import type { WorldLookup } from '../../../utils/progressionLookup';
import type { CharacterClass } from '../../../types/progression';
type Props = {
  cls?: CharacterClass;
  list: Checklist;
  lookup?: WorldLookup;
  status: string;
};

/** Marlon: misión 2 (segundas clases) y misión 3 (solo Blade Knight). */
export function MarlonSection({
  cls,
  list,
  lookup,
  status
}: Props) {
  const npcLoc = useNpcLocation('marlon');
  const npcRow = npcLoc ? npcLocationValidation(npcLoc) : {
    status: 'pendiente' as const,
    detail: ''
  };
  if (cls && !cls.marlon) {
    return <GuidePanel id="marlon" eyebrow="Misión" title={questTitle(2)} data-id="element-760" data-mp="36ls4hhovwey0">
        <Note data-id="element-761" data-mp="3amkfafdemrwe">{cls.base} no tiene reglas de inicio para las misiones de Marlon en la configuración.</Note>
      </GuidePanel>;
  }
  const showCombo = !cls || cls.combo;
  return <>
      <GuidePanel id="marlon" eyebrow="Misión" title={questTitle(2)} data-id="element-762" data-mp="3kc3wzhpdsfpo">
        <ValidationSummary rows={[{
        label: 'Nivel, Zen, misión previa y clases',
        status: 'documentado',
        detail: 'Confirmado por la configuración exportada'
      }, {
        label: 'Objeto y cantidad',
        status: 'documentado',
        detail: 'quest_objectives (ítem con su nivel exacto)'
      }, {
        label: 'Dónde se obtiene',
        status: 'documentado',
        detail: 'Drop de misión condicionado a la misión aceptada'
      }, {
        label: 'Recompensa o beneficio',
        status: 'pendiente',
        detail: 'Los archivos de misiones no lo incluyen'
      }, {
        label: 'Ubicación de Marlon',
        ...npcRow
      }, {
        label: 'Prueba dentro del juego',
        status: 'sin-probar'
      }]} data-id="element-763" data-mp="3ke81o1v3tir0" />
        <Note data-id="element-764" data-mp="32bswso5phm5w">
          Marlon no es una evolución de clase: son misiones posteriores a Sebina. El orden y los nombres (Ring of Honor y Dark Stone) siguen la{' '}
          <a href={MARLON_REFERENCE_URL} target="_blank" rel="noreferrer" className="font-semibold text-[#D4AF37] underline underline-offset-4" data-id="element-765" data-mp="3c72xn8kdbni3">guía oficial de Webzen</a>; nivel, Zen y requisitos son los de Vanguard.
        </Note>
        <QuestBlock questId={2} cls={cls} list={list} prefix="marlon-2" lookup={lookup} status={status} steps={marlon.steps} data-id="element-766" data-mp="3l6g9g7vyso5z" />
      </GuidePanel>

      {showCombo && <GuidePanel id="combo" eyebrow="Solo Blade Knight" title={questTitle(3)} data-id="element-767" data-mp="3uzopziw2gvxx">
          <SubHeading data-id="element-768" data-mp="crzbru3k9wd6">1. Completar la misión</SubHeading>
          <QuestBlock questId={3} cls={cls} list={list} prefix="marlon-3" lookup={lookup} status={status} steps={marlon.comboSteps} data-id="element-769" data-mp="cqecxsa7rr9i" />
          <SubHeading data-id="element-770" data-mp="rexvt4039ohv">2. Aprender a ejecutar el combo</SubHeading>
          <div className="rounded-2xl border border-white/10 bg-[#0A1628] p-3.5" data-id="element-771" data-mp="rdmxhjpex8y5">
            <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-772" data-mp="wcga79wvkvx9">
              <p className="text-sm font-semibold text-white" data-id="element-773" data-mp="ie852epoqob0">Secuencia de habilidades y condiciones</p>
              <ValidationBadge status="pendiente" data-id="element-774" data-mp="mes2lje67txo" />
            </div>
            <ol className="mt-3 grid grid-cols-3 gap-2" aria-label="Secuencia del combo" data-id="element-775" data-mp="8oojh1al8gcg">
              {[1, 2, 3].map(n => <li key={n} className="flex min-h-[64px] flex-col items-center justify-center rounded-xl border border-dashed border-white/15 text-center" data-id="element-776" data-mp="dh9g0akvp7jo">
                  <span className="font-cinzel text-lg font-black text-gray-500" data-id="element-777" data-mp="3pc0wsqr1vfo">{n}</span>
                  <span className="text-[11px] text-gray-500" data-id="element-778" data-mp="3lvq9ec691d6o">Habilidad</span>
                </li>)}
            </ol>
            <p className="mt-2 text-xs text-gray-400" data-id="element-779" data-mp="3qt9pziqf621s">Completar la misión y saber ejecutar el combo son cosas distintas. Los archivos de Vanguard no documentan la secuencia de habilidades, sus tiempos ni sus condiciones; se publicarán cuando estén confirmados.</p>
          </div>
        </GuidePanel>}
    </>;
}